import { useToast } from '@/_shared/hooks/toast.js';
import { Button, Skeleton, SkeletonItem, Tooltip, mergeClasses } from '@fluentui/react-components';
import { CopyRegular } from '@fluentui/react-icons';
import { trackException } from '@this/app-shared/dist/tools/insights/track.js';
import { type ReactNode, useCallback } from 'react';
import { useSharedStyles } from '../shared-styles.js';
import { useStyles } from './styles.js';

type FieldValue = string | number | null;

type FieldValuePropsBase<TValue extends FieldValue> = {
    label: string;
    value?: TValue;
    copyableValue?: string | number | null;
    colSpan?: 2 | 'full';
    afterContent?: ReactNode;
    tooltip?: string;
    indicateError?: boolean;
};

type FieldValuesActions<TValue extends FieldValue> = FieldValuePropsBase<TValue> &
    (
        | {
              onValueClick?: () => void;
              valueLinkURL?: never;
              valueLinkTarget?: never;
          }
        | {
              onValueClick?: never;
              valueLinkURL?: string;
              valueLinkTarget?: '_blank' | '_self';
          }
    );

type FieldValueProps<TValue extends FieldValue> = TValue extends null
    ? FieldValuesActions<TValue> & {
          nullPlaceholder: string;
      }
    : FieldValuesActions<TValue> & {
          nullPlaceholder?: string;
      };

export function FieldValue<TValue extends FieldValue>({
    label,
    value,
    copyableValue,
    colSpan,
    afterContent,
    tooltip,
    indicateError,
    nullPlaceholder,
    onValueClick,
    valueLinkURL,
    valueLinkTarget,
}: FieldValueProps<TValue>) {
    const styles = useStyles();
    const buttonContainerStyles = useSharedStyles();

    const toast = useToast();

    const onCopyButtonClick = useCallback(() => {
        if (copyableValue) {
            navigator.clipboard
                .writeText(copyableValue.toString())
                .then(() => {
                    toast('Wert kopiert', 'Der Wert wurde in die Zwischenablage kopiert', {
                        intent: 'success',
                    });
                })
                .catch((error) => {
                    trackException(error);
                    toast('Fehler beim Kopieren', 'Der Wert konnte nicht in die Zwischenablage kopiert werden', {
                        intent: 'error',
                    });
                });
        }
    }, [copyableValue, toast]);

    return (
        <div
            data-field-value
            className={mergeClasses(
                styles.container,
                colSpan === 2 && styles.span2,
                colSpan === 'full' && styles.spanAll
            )}
        >
            <div className={styles.label}>{label}</div>

            {value === undefined ? (
                <Skeleton>
                    <SkeletonItem className={styles.skeletonItem} />
                </Skeleton>
            ) : (
                <div className={styles.valueContainer}>
                    <span
                        className={mergeClasses(
                            onValueClick !== undefined || valueLinkURL !== undefined ? styles.valueAction : undefined,
                            indicateError ? styles.valueError : undefined
                        )}
                        onClick={onValueClick}
                    >
                        <Tooltip
                            visible={!tooltip ? false : undefined}
                            content={tooltip ?? ''}
                            relationship="label"
                            positioning="below-start"
                        >
                            {valueLinkURL ? (
                                <a
                                    aria-disabled={true}
                                    href={valueLinkURL}
                                    target={valueLinkTarget}
                                    rel={valueLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                                >
                                    {value ?? nullPlaceholder}
                                </a>
                            ) : (
                                <span>{value ?? nullPlaceholder}</span>
                            )}
                        </Tooltip>
                    </span>

                    <div className={styles.valueActions}>
                        {copyableValue && (
                            <div className={buttonContainerStyles.container}>
                                <Button
                                    className={buttonContainerStyles.button}
                                    appearance="subtle"
                                    size="small"
                                    icon={<CopyRegular />}
                                    onClick={onCopyButtonClick}
                                />
                            </div>
                        )}

                        {afterContent}
                    </div>
                </div>
            )}
        </div>
    );
}
